import PieChart from "@/components/Charts/PieChart";
import DataListCard from "@/components/Charts/DataListCard";
import CardWrapper from "@/components/CardWrapper";
import PurchasesOverTime from "@/components/Charts/PurchasesOverTime";
import MonthlySalesBarChart from "@/components/Charts/MonthlySalesBarChart";
import { countSimilarKeys } from "@/util/countSimilarKeys";
import { getTotalPurchases, getTotalEarned } from "@/util/getTotals";
import { getMonthlySales } from "@/util/getMonthlySoldTotals";
import { getChartColors } from "@/config/chartColors";
import { DbItemSelect } from "@server/db/schema";
import { ListingWithItems, PurchaseWithItems } from "@/types";
import { getMoneyCardData } from "@/components/Charts/chartUtils/moneyCardData";
import { getPurchaseCardData } from "@/components/Charts/chartUtils/getPurchaseCardData";
import ChartSkeleton from "@/components/Skeletons/ChartSkeleton";
import HighestSoldItem from "@/components/Charts/HighestSoldItem";
import HighestPurchaseCard from "@/components/Charts/HighestPurchaseCard";
import { useTanFetch } from "@/hooks/useTanFetch";
import { ChartData, ApiResponse, User } from "@/types";
import { useState } from "react";
import { getEarliestYear } from "@/util/getEarliestYear";
import { UpgradePrompt } from "@/components/UpgradePrompt";
import { useNavigate } from "react-router";

export default function ChartPage() {
  const { data: dbRes, isLoading } = useTanFetch<ChartData>(["chart-data"]);
  const { data: userData } = useTanFetch<ApiResponse<User>>(["user"], true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const navigate = useNavigate();

  const { colors: chartColors, grid: gridColor } = getChartColors();

  const subscriptionTier = userData?.data?.subscriptionStatus === 'ACTIVE' ? 'ACTIVE' : 'FREE';

  const handleUpgrade = () => {
    navigate('/subscribe');
  };

  if (isLoading || !dbRes?.allTableData) {
    return <ChartSkeleton />;
  }

  const {
    dbPurchaseData,
    dbListingData,
    dbAllItems,
  }: {
    dbPurchaseData: PurchaseWithItems[];
    dbListingData: ListingWithItems[];
    dbAllItems: DbItemSelect[];
  } = dbRes.allTableData;

  const earliestYear = getEarliestYear(
    dbPurchaseData,
    dbListingData,
    dbAllItems,
  );

  const handleYearChange = (year: number) => {
    setSelectedYear(year);
  };

  const hasPurchases = dbPurchaseData.length > 0;
  const uniqueKeys = countSimilarKeys(dbPurchaseData, "source");
  const totalSpent = getTotalPurchases(dbPurchaseData) || 0;
  const totalEarned = getTotalEarned(dbListingData) || 0;
  const monthlySoldTotals = getMonthlySales(dbListingData, selectedYear) || [];
  const purchaseCardData = getPurchaseCardData(
    dbPurchaseData,
    dbListingData,
    dbAllItems,
  );
  const moneyCardData = getMoneyCardData(totalSpent, totalEarned);

  const listedSoldItemData = [
    {
      name: "Unlisted",
      count:
        dbAllItems.filter(
          (item: DbItemSelect) => !item.isSold && !item.isListed,
        ).length || 0,
    },
    {
      name: "Listed",
      count:
        dbAllItems.filter((item: DbItemSelect) => item.isListed).length || 0,
    },
    {
      name: "Sold",
      count: dbAllItems.filter((item: DbItemSelect) => item.isSold).length || 0,
    },
  ];

  return (
    <div className="px-2 py-2 pb-20 space-y-4">
      {/* Upgrade Prompt */}
      <UpgradePrompt
        subscriptionTier={subscriptionTier}
        onUpgrade={handleUpgrade}
        variant="banner"
        className="mb-4"
      />

      {/* Dashboard Grid */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-6">
        <div className="col-span-3 md:col-span-2">
        <div className="flex gap-4 md:flex-col">
          <DataListCard data={purchaseCardData} />
          <DataListCard data={moneyCardData} />
        </div>
      </div>

      <div className="col-span-3 flex md:col-span-4">
        <div className="flex grow gap-4">
          <HighestSoldItem
            dbPurchaseData={dbPurchaseData}
            dbListingData={dbListingData}
            dbAllItems={dbAllItems}
          />
          <HighestPurchaseCard dbPurchaseData={dbPurchaseData} />
        </div>
      </div>

      {/* Monthly Purchases/Listings/Sold */}
      <div className="col-span-3 h-full w-full">
        <PurchasesOverTime
          purchaseData={dbPurchaseData}
          listingData={dbListingData}
          itemData={dbAllItems}
          colors={chartColors}
          gridColor={gridColor}
          selectedYear={selectedYear}
          onYearChange={handleYearChange}
          minYear={earliestYear}
        />
      </div>

      {/* Monthly Sales */}
      <div className="col-span-3 h-full w-full">
        <MonthlySalesBarChart
          salesData={monthlySoldTotals}
          colors={chartColors}
          gridColor={gridColor}
          selectedYear={selectedYear}
          onYearChange={handleYearChange}
          minYear={earliestYear}
        />
      </div>

      {/* Pie Charts */}
      {hasPurchases && (
        <>
          <CardWrapper
            contentClasses="flex justify-center items-center flex-col"
            title="Items"
            content={
              <PieChart data={listedSoldItemData} colors={chartColors} />
            }
          />
          <CardWrapper
            contentClasses="flex justify-center items-center flex-col"
            title="Sources"
            content={<PieChart data={uniqueKeys} colors={chartColors} />}
          />
        </>
      )}
      </div>
    </div>
  );
}
